/**
 * 「憑證」小元件(REQ-20260907002 §2.4)—— 現付收據用的**輕量**入口:
 * 列上一顆小按鈕 + 點開的 Modal。
 *
 * ⚠️ 需求原文明確要求「**兩者不要混在同一個 UI 元件裡**」:
 * 單據附件卡片(`DocumentAttachmentCard`)是整張單的檔案櫃 —— 標題、分類 chip、
 * 大縮圖;現付收據是明細列上一格,只要「有沒有、幾張、點開能不能加一張」。
 * 兩者共用的是**資料表與端點**(同一張 `document_attachments`),不是版面。
 *
 * 兩個掛法:
 *  - 明細列的憑證:`docType='PURCHASE_ORDER_LINE'` + `docId=lineId`
 *  - 付款方式卡右邊的「收據憑證 N 張」:`docType='PURCHASE_ORDER'` +
 *    `docId=orderId` + `category='SIGNED_RECEIPT'`(上傳時就帶分類 ——
 *    這一顆問的就是收據,不必再讓使用者選一次)
 *
 * 沒有 `docId`(建單模式,單 / 明細都還不存在)時**照樣渲染**,只是按鈕停用 ——
 * 與附件卡片同一種態度,不為它改建單流程。
 */
import { useRef, useState } from 'react';
import {
  DOCUMENT_ATTACHMENT_ACCEPT,
  type DocumentAttachmentCategory,
  type DocumentType,
} from '@app/shared';
import { Modal } from './Modal';
import {
  deleteDocumentAttachment,
  uploadDocumentAttachment,
  useDocumentAttachmentUrl,
  useDocumentAttachments,
} from '../features/document-attachments/api';

interface Props {
  docType: DocumentType;
  docId: number | undefined;
  /** 上傳時直接帶的分類。單頭的「收據憑證」帶 `SIGNED_RECEIPT`,明細列不帶 */
  category?: DocumentAttachmentCategory | null;
  /** 唯讀原因(單已送出確認 / 還沒建立)。有值時只能看不能改 */
  // `| undefined` 是 exactOptionalPropertyTypes 的要求 —— 呼叫端會轉傳可能是
  // undefined 的值(同 REQ-20260905006 踩過的那個)
  disabledReason?: string | undefined;
  /**
   * 按鈕文字。**有傳時後面會自動接上張數**(「收據憑證 2 張」);
   * 不傳時是明細列那種「▣ N」/「—」的極簡樣子 —— 那一格窄,放不下字。
   */
  label?: string;
  testId?: string;
}

export function DocumentReceiptButton({
  docType,
  docId,
  category = null,
  disabledReason,
  label,
  testId,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const listQuery = useDocumentAttachments(docType, docId);
  const items = listQuery.data ?? [];
  const canEdit = docId != null && disabledReason == null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={docId == null}
        data-testid={testId}
        className="rounded border px-1.5 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50 disabled:text-slate-300"
      >
        {label != null ? `${label} ${items.length} 張` : items.length > 0 ? `▣ ${items.length}` : '—'}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="憑證" widthClass="max-w-md">
        <ReceiptModalBody
          docType={docType}
          docId={docId}
          category={category}
          canEdit={canEdit}
          disabledReason={docId == null ? '建立草稿後即可上傳' : disabledReason}
        />
      </Modal>
    </>
  );
}

function ReceiptModalBody({
  docType,
  docId,
  category,
  canEdit,
  disabledReason,
}: {
  docType: DocumentType;
  docId: number | undefined;
  category: DocumentAttachmentCategory | null;
  canEdit: boolean;
  disabledReason?: string | undefined;
}): JSX.Element {
  const listQuery = useDocumentAttachments(docType, docId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const items = listQuery.data ?? [];

  /** 重取清單用這支 query 自己的 `refetch()`,理由見 `DocumentAttachmentCard` */
  const refresh = (): void => {
    void listQuery.refetch?.();
  };

  const onPick = async (file: File | undefined): Promise<void> => {
    if (!file || docId == null) return;
    setError(null);
    setUploading(true);
    try {
      await uploadDocumentAttachment(docType, docId, file, category);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : '上傳失敗');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onRemove = async (id: number, name: string): Promise<void> => {
    if (docId == null) return;
    // 二次確認沿用附件區既有的做法(REQ-20260819007)
    if (!window.confirm(`刪除「${name}」？`)) return;
    setError(null);
    try {
      await deleteDocumentAttachment(docType, docId, id);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : '刪除失敗');
    }
  };

  return (
    <div className="grid gap-2 text-sm">
      {items.length === 0 ? (
        <p className="text-slate-500">尚未上傳憑證。</p>
      ) : (
        <ul className="grid gap-1">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-2 border rounded px-2 py-1">
              <ReceiptThumbnail docType={docType} docId={docId} attachmentId={a.id} name={a.originalName} />
              <span className="truncate flex-1">{a.originalName}</span>
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => void onRemove(a.id, a.originalName)}
                  className="text-red-600 hover:underline text-xs"
                >
                  刪除
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <label className="grid gap-1 text-xs text-slate-600">
          新增憑證(圖片或 PDF,單檔 10MB 以內)
          <input
            ref={fileInput}
            type="file"
            accept={DOCUMENT_ATTACHMENT_ACCEPT}
            disabled={uploading}
            onChange={(e) => void onPick(e.target.files?.[0])}
            className="border rounded-md px-2 py-1 text-sm disabled:opacity-50"
          />
        </label>
      ) : (
        <p className="text-[11px] text-slate-400">{disabledReason}</p>
      )}

      {uploading ? (
        <div role="status" className="text-slate-500 text-xs">
          上傳中...
        </div>
      ) : null}
      {error ? (
        <div role="alert" className="text-red-600 text-xs">
          {error}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 憑證的小縮圖。**刻意比附件卡片簡單** —— 只有圖片出縮圖,PDF / 其他一律
 * 文字連結在新分頁開。收據是一張照片,為它再拉一次 pdf.js 不划算。
 */
function ReceiptThumbnail({
  docType,
  docId,
  attachmentId,
  name,
}: {
  docType: DocumentType;
  docId: number | undefined;
  attachmentId: number;
  name: string;
}): JSX.Element {
  const urlQuery = useDocumentAttachmentUrl(docType, docId, attachmentId);
  if (!urlQuery.data) return <span className="shrink-0 w-8 h-8 bg-slate-100 rounded" />;
  return (
    <a href={urlQuery.data} target="_blank" rel="noopener noreferrer" className="shrink-0">
      <img src={urlQuery.data} alt={name} className="w-8 h-8 object-cover rounded bg-slate-100" />
    </a>
  );
}
